<script setup lang="ts">
import { computed, ref } from "vue";
import {
  AlertOctagon,
  CalendarDays,
  EyeOff,
  GripVertical,
  UserRound,
} from "lucide-vue-next";
import { useWorkspaceStore } from "@/stores/workspace";
import { shortDate } from "@/utils/dates";
import { detailSectionForStatusChange } from "@/utils/task-status";
import type { ProjectTask, TaskStatus } from "@/types/models";

const props = defineProps<{ tasks: ProjectTask[] }>();
const emit = defineEmits<{
  openTask: [id: string, section?: string, status?: TaskStatus];
}>();
const workspace = useWorkspaceStore();
const dragged = ref<string | null>(null);
const columns: TaskStatus[] = [
  "Blocked",
  "Not started",
  "In progress",
  "In review",
  "Complete",
  "Not applicable",
];
const grouped = computed(() =>
  columns.map((status) => ({
    status,
    tasks: props.tasks.filter(
      (task) => task.status === status && task.type !== "summary",
    ),
  })),
);
function drop(status: TaskStatus) {
  const task = props.tasks.find((item) => item.id === dragged.value);
  if (task && canChangeTaskStatus(task) && !statusUnavailable(task, status))
    changeStatus(task, status);
  dragged.value = null;
}
function itemTypeLabel(task: ProjectTask): string {
  if (task.type === "checklist") return "Checklist";
  if (task.type === "questionnaire") return "Questionnaire";
  if (task.type === "milestone") return "Milestone";
  return "Task";
}
function statusUnavailable(task: ProjectTask, status: TaskStatus): boolean {
  return (
    status === "Complete" &&
    (task.type === "checklist" ||
      (task.type === "questionnaire" && task.status !== "In review"))
  );
}

function canChangeTaskStatus(task: ProjectTask) {
  return workspace.canEditPlan && task.type !== "summary";
}

async function changeStatus(task: ProjectTask, status: TaskStatus) {
  if (!canChangeTaskStatus(task)) return false;
  if (status === task.status) return true;
  const section = detailSectionForStatusChange(
    task,
    status,
    workspace.active?.tasks || props.tasks,
  );
  if (section) {
    emit("openTask", task.id, section, status);
    return true;
  }
  return Boolean(await workspace.setTaskStatus(task.id, status));
}

async function statusSelected(task: ProjectTask, event: Event) {
  const select = event.target as HTMLSelectElement;
  const status = select.value as TaskStatus;
  const section = detailSectionForStatusChange(
    task,
    status,
    workspace.active?.tasks || props.tasks,
  );
  if (section) select.value = task.status;
  const changed = await changeStatus(task, status);
  if (!changed) select.value = task.status;
}
</script>

<template>
  <div class="kanban-wrap">
    <div class="kanban" aria-label="Plan grouped by status">
      <section
        v-for="column in grouped"
        :key="column.status"
        class="kanban-column"
        @dragover.prevent
        @drop="drop(column.status)"
      >
        <header>
          <span
            class="column-status"
            :class="`column-status--${column.status.toLowerCase().replace(' ', '-')}`"
          ></span>
          <h3>{{ column.status }}</h3>
          <span>{{ column.tasks.length }}</span>
        </header>
        <div class="kanban-cards">
          <article
            v-for="task in column.tasks"
            :key="task.id"
            class="kanban-card"
            :class="{
              'kanban-card--dragging': dragged === task.id,
              'kanban-card--internal': task.visibility === 'Internal',
            }"
            draggable="true"
            tabindex="0"
            @dragstart="dragged = task.id"
            @dragend="dragged = null"
            @keydown.enter="emit('openTask', task.id)"
          >
            <button type="button" @click="emit('openTask', task.id)">
              <span class="kanban-card__head"
                ><small
                  >{{
                    workspace.active?.sections.find(
                      (section) => section.id === task.sectionId,
                    )?.name
                  }}
                  · {{ itemTypeLabel(task) }}</small
                ><GripVertical :size="15" aria-hidden="true" /></span
              ><span class="kanban-card__title"
                ><strong>{{ task.title }}</strong
                ><EyeOff
                  v-if="workspace.isInternal && task.visibility === 'Internal'"
                  :size="13"
                  aria-label="Internal — visible only to VIPdesk project participants"
                  title="Internal — visible only to VIPdesk project participants" /></span
              ><span v-if="task.status === 'Blocked'" class="blocker-line"
                ><AlertOctagon :size="14" />{{ task.blocker }}</span
              ><span class="kanban-card__meta"
                ><span><UserRound :size="13" />{{ task.ownerName }}</span
                ><span
                  ><CalendarDays :size="13" />{{
                    shortDate(task.dueDate)
                  }}</span
                ></span
              ></button
            ><label
              ><span class="sr-only">Move {{ task.title }} to status</span
              ><select
                :value="task.status"
                :disabled="!canChangeTaskStatus(task)"
                :title="
                  canChangeTaskStatus(task)
                    ? undefined
                    : 'Only the task owner can change this status.'
                "
                @change="statusSelected(task, $event)"
              >
                <option
                  v-for="status in columns"
                  :key="status"
                  :disabled="statusUnavailable(task, status)"
                >
                  {{ status }}
                </option>
              </select></label
            >
          </article>
          <div v-if="!column.tasks.length" class="kanban-empty">
            No {{ column.status.toLowerCase() }} work in this view.
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.kanban-wrap {
  height: 100%;
  overflow: auto;
  padding: 16px;
  background: var(--paper);
}
.kanban {
  min-width: 1100px;
  min-height: 100%;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
}
.kanban-column {
  min-width: 0;
  min-height: 100%;
  border: 1px solid var(--rule);
  border-radius: 7px;
  background: #f7f9f8;
}
.kanban-column > header {
  min-height: 46px;
  display: grid;
  grid-template-columns: 9px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-bottom: 1px solid var(--rule);
}
.kanban-column > header > span:last-child {
  min-width: 22px;
  padding: 2px 6px;
  border-radius: 10px;
  background: #e1e7e5;
  color: var(--text-soft);
  font-size: 11px;
  text-align: center;
}
.column-status {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #9aa6a3;
}
.column-status--in-progress,
.column-status--in-review {
  background: var(--shared);
}
.column-status--blocked {
  background: var(--danger);
}
.column-status--complete {
  background: var(--success);
}
.column-status--not-applicable {
  border: 1px solid var(--text-faint);
  background: transparent;
}
.kanban-cards {
  min-width: 0;
  display: grid;
  align-content: start;
  gap: 9px;
  padding: 9px;
}
.kanban-card {
  min-width: 0;
  position: relative;
  border: 1px solid var(--rule);
  border-radius: 6px;
  background: white;
  transition:
    opacity 160ms var(--ease),
    box-shadow 160ms var(--ease);
}
.kanban-card:hover {
  border-color: var(--rule-strong);
  box-shadow: 0 5px 16px rgba(8, 27, 46, 0.08);
}
.kanban-card--dragging {
  opacity: 0.5;
}
.kanban-card--internal {
  background: #fbf9fc;
}
.kanban-card > button {
  min-width: 0;
  width: 100%;
  display: grid;
  gap: 9px;
  padding: 11px;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.kanban-card__head {
  min-width: 0;
  display: flex;
  justify-content: space-between;
  color: var(--text-faint);
}
.kanban-card__head small {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.kanban-card > button strong,
.kanban-card__meta span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.kanban-card__title {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 7px;
}
.kanban-card__title strong {
  min-width: 0;
}
.kanban-card__title svg {
  flex: 0 0 auto;
  margin-top: 2px;
  color: var(--internal);
}
.kanban-card__meta {
  display: grid;
  gap: 4px;
  color: var(--text-soft);
  font-size: 11px;
}
.kanban-card__meta span {
  display: flex;
  align-items: center;
  gap: 5px;
}
.blocker-line {
  display: -webkit-box;
  overflow: hidden;
  color: var(--danger);
  font-size: 11px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.blocker-line svg {
  display: inline;
  vertical-align: -2px;
  margin-right: 4px;
}
.kanban-card > label {
  display: block;
  padding: 0 10px 10px;
}
.kanban-card select {
  min-width: 0;
  width: 100%;
  height: 29px;
  border: 1px solid var(--rule);
  border-radius: 4px;
  background: var(--sheet-subtle);
  color: var(--text-soft);
  font-size: 11px;
}
.kanban-empty {
  min-height: 90px;
  display: grid;
  place-items: center;
  padding: 12px;
  border: 1px dashed var(--rule-strong);
  border-radius: 5px;
  color: var(--text-faint);
  font-size: 11px;
  text-align: center;
}
@media (max-width: 700px) {
  .kanban {
    width: max-content;
    min-width: 100%;
    grid-template-columns: repeat(6, min(82vw, 320px));
    scroll-snap-type: x mandatory;
  }
  .kanban-column {
    scroll-snap-align: start;
  }
}
</style>
